import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../store";
import {
  fetchBoards,
  fetchRounds,
  getBoards,
  getRounds,
  getTournoi,
} from "../../../../features/tournoi/tournoiSlice";
import {
  fetchPlayersWithRegistrations,
  getPlayersWithRegistrations,
} from "../../../../features/player/playerSlice";
import { showNotification } from "../../../../features/globalNotification/globalNotificationSlice";
import {
  SpiddDraftMatch,
  SpiddDraftMatchStatus,
} from "../../../../types/SpiddDraftMatch";
import {
  callSpiddDraftBatchCreate,
  callSpiddDraftBatchRemap,
  callSpiddDraftCreateMatch,
  callSpiddDraftIgnore,
  callSpiddDraftLinkMatch,
  callSpiddDraftSyncRun,
  listenSpiddDraftMatches,
  summarizeDrafts,
} from "../../../../features/spiddDraftMatches/spiddDraftApi";
import {
  defaultSpiddDraftFilters,
  filterSpiddDrafts,
  isBatchCreatable,
  isRemappable,
  SpiddDraftFilters,
} from "../../../../features/spiddDraftMatches/filterDrafts";
import {
  boardDisplayName,
  compareDrafts,
  draftPhaseLabel,
  draftSuggestionLabel,
  draftTypeLabel,
  playerDisplayName,
  SpiddDraftSortDir,
  SpiddDraftSortKey,
} from "../../../../features/spiddDraftMatches/draftDisplay";

const STATUS_LABELS: Record<SpiddDraftMatchStatus, string> = {
  PROPOSED: "Proposée",
  NEEDS_REVIEW: "À revoir",
  APPROVED: "Approuvée",
  LINKED: "Associée",
  IGNORED: "Ignorée",
  STALE: "Obsolète",
};

type SortableColumn = {
  id: SpiddDraftSortKey;
  label: string;
};

const SORTABLE_COLUMNS: SortableColumn[] = [
  { id: "tableau", label: "Tableau" },
  { id: "type", label: "Type" },
  { id: "phase", label: "Poule / tour" },
  { id: "players", label: "Joueurs" },
  { id: "status", label: "Statut" },
  { id: "suggestion", label: "Suggestion" },
  { id: "alerts", label: "Alertes" },
];

const SpiddDraftsPanel: FC = () => {
  const dispatch = useAppDispatch();
  const tournoi = useAppSelector(getTournoi);
  const boards = useAppSelector(getBoards);
  const rounds = useAppSelector(getRounds);
  const players = useAppSelector(getPlayersWithRegistrations);
  const tournoiId = tournoi?.id ?? null;

  const [drafts, setDrafts] = useState<SpiddDraftMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SpiddDraftFilters>(
    defaultSpiddDraftFilters
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [batchConfirmOpen, setBatchConfirmOpen] = useState(false);
  const [remapOpen, setRemapOpen] = useState(false);
  const [remapTableauId, setRemapTableauId] = useState("");
  const [remapTourId, setRemapTourId] = useState("");
  const [detail, setDetail] = useState<SpiddDraftMatch | null>(null);
  const [sortKey, setSortKey] = useState<SpiddDraftSortKey>("tableau");
  const [sortDir, setSortDir] = useState<SpiddDraftSortDir>("asc");

  useEffect(() => {
    dispatch(fetchBoards());
    dispatch(fetchRounds());
    dispatch(fetchPlayersWithRegistrations());
  }, [dispatch]);

  useEffect(() => {
    if (!tournoiId) return;
    setLoading(true);
    const unsub = listenSpiddDraftMatches(
      tournoiId,
      (rows) => {
        setDrafts(rows);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return unsub;
  }, [tournoiId]);

  const boardsById = useMemo(() => {
    const map = new Map<string, (typeof boards)[number]>();
    for (const board of boards) {
      if (board.id) map.set(String(board.id), board);
    }
    return map;
  }, [boards]);

  const playersById = useMemo(() => {
    const map = new Map<string, (typeof players)[number]>();
    for (const player of players) {
      if (player.id) map.set(String(player.id), player);
    }
    return map;
  }, [players]);

  const playersByLicence = useMemo(() => {
    const map = new Map<string, (typeof players)[number]>();
    for (const player of players) {
      const licence = String(player.licenceId || "").trim();
      if (licence && !map.has(licence)) map.set(licence, player);
    }
    return map;
  }, [players]);

  const getTableauLabel = (draft: SpiddDraftMatch): string => {
    const board = draft.firestoreTableauId
      ? boardsById.get(String(draft.firestoreTableauId))
      : undefined;
    const label = boardDisplayName(board);
    if (label !== "—") return label;
    return draft.sourceTableauId || "—";
  };

  const getPlayersLabel = (draft: SpiddDraftMatch): string => {
    return (draft.playerLicences || [])
      .map((licence, index) => {
        const playerId = draft.playerIds?.[index];
        const player =
          (playerId ? playersById.get(String(playerId)) : undefined) ||
          playersByLicence.get(String(licence));
        return playerDisplayName(player, licence);
      })
      .join(", ");
  };

  const summary = useMemo(() => summarizeDrafts(drafts), [drafts]);

  const filtered = useMemo(() => {
    const rows = filterSpiddDrafts(drafts, filters, {
      getSearchText: (draft) =>
        `${getTableauLabel(draft)} ${getPlayersLabel(draft)}`,
    });
    return [...rows].sort((a, b) =>
      compareDrafts(a, b, sortKey, sortDir, getTableauLabel, getPlayersLabel)
    );
    // getTableauLabel / getPlayersLabel depend on boards/players maps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts, filters, sortKey, sortDir, boardsById, playersById, playersByLicence]);

  const tableauOptions = useMemo(() => {
    const bySource = new Map<string, string>();
    for (const draft of drafts) {
      if (!bySource.has(draft.sourceTableauId)) {
        bySource.set(draft.sourceTableauId, getTableauLabel(draft));
      }
    }
    return Array.from(bySource.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], "fr", { sensitivity: "base", numeric: true })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drafts, boardsById]);

  const phaseOptions = useMemo(() => {
    const set = new Set(
      drafts.map((d) => d.sourcePhaseId).filter(Boolean) as string[]
    );
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, "fr", { sensitivity: "base", numeric: true })
    );
  }, [drafts]);

  const poolOptions = useMemo(() => {
    const entries = new Map<string, string>();
    for (const draft of drafts) {
      if (!draft.sourcePoolId) continue;
      entries.set(
        draft.sourcePoolId,
        draft.meta?.poolLabel || draft.sourcePoolId
      );
    }
    return Array.from(entries.entries()).sort((a, b) =>
      a[1].localeCompare(b[1], "fr", { sensitivity: "base", numeric: true })
    );
  }, [drafts]);

  const handleSort = (column: SpiddDraftSortKey) => {
    if (sortKey === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(column);
    setSortDir("asc");
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectReadyVisible = () => {
    const next = new Set<string>();
    for (const d of filtered) {
      if (isBatchCreatable(d)) next.add(d.id);
    }
    setSelected(next);
  };

  const selectedRemappableIds = useMemo(
    () =>
      Array.from(selected).filter((id) => {
        const d = drafts.find((x) => x.id === id);
        return d && isRemappable(d);
      }),
    [selected, drafts]
  );

  const openRemapDialog = () => {
    const rows = selectedRemappableIds
      .map((id) => drafts.find((d) => d.id === id))
      .filter((d): d is SpiddDraftMatch => Boolean(d));
    const tableauIds = Array.from(
      new Set(
        rows
          .map((d) => d.firestoreTableauId)
          .filter((id): id is string => Boolean(id))
      )
    );
    const tourIds = Array.from(
      new Set(
        rows
          .map((d) => d.firestoreTourId)
          .filter((id): id is string => Boolean(id))
      )
    );
    setRemapTableauId(tableauIds.length === 1 ? tableauIds[0] : "");
    setRemapTourId(tourIds.length === 1 ? tourIds[0] : "");
    setRemapOpen(true);
  };

  const sortedBoards = useMemo(
    () =>
      [...boards].sort((a, b) =>
        boardDisplayName(a).localeCompare(boardDisplayName(b), "fr", {
          sensitivity: "base",
          numeric: true,
        })
      ),
    [boards]
  );

  const sortedRounds = useMemo(
    () => [...rounds].sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0)),
    [rounds]
  );

  const runAction = async (label: string, fn: () => Promise<void>) => {
    if (!tournoiId) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      dispatch(
        showNotification({
          message: label,
          severity: "success",
          autoHideDuration: 3000,
        })
      );
    } catch (e) {
      const message = e instanceof Error ? e.message : "Erreur";
      setError(message);
      dispatch(
        showNotification({
          message,
          severity: "error",
          autoHideDuration: 5000,
        })
      );
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = (draft: SpiddDraftMatch) =>
    runAction("Match créé", async () => {
      if (!tournoiId) return;
      await callSpiddDraftCreateMatch({
        tournoiId,
        draftId: draft.id,
        expectedRevision: draft.revision,
      });
    });

  const handleLink = (draft: SpiddDraftMatch) =>
    runAction("Match associé", async () => {
      if (!tournoiId || !draft.suggestedOperationalMatchId) return;
      await callSpiddDraftLinkMatch({
        tournoiId,
        draftId: draft.id,
        matchId: draft.suggestedOperationalMatchId,
        expectedRevision: draft.revision,
      });
    });

  const handleIgnore = (draft: SpiddDraftMatch) =>
    runAction("Proposition ignorée", async () => {
      if (!tournoiId) return;
      await callSpiddDraftIgnore({ tournoiId, draftId: draft.id });
    });

  const handleSync = () =>
    runAction("Synchronisation terminée", async () => {
      if (!tournoiId) return;
      await callSpiddDraftSyncRun({ tournoiId });
    });

  const handleBatchCreate = () =>
    runAction("Création en lot terminée", async () => {
      if (!tournoiId) return;
      const ids = Array.from(selected).filter((id) => {
        const d = drafts.find((x) => x.id === id);
        return d && isBatchCreatable(d);
      });
      const result = await callSpiddDraftBatchCreate({
        tournoiId,
        draftIds: ids,
      });
      setBatchConfirmOpen(false);
      setSelected(new Set());
      if (result.errors.length) {
        setError(
          `${result.created.length} créés, ${result.errors.length} erreurs`
        );
      }
    });

  const handleBatchRemap = () =>
    runAction("Mapping corrigé", async () => {
      if (!tournoiId) return;
      if (!remapTableauId || !remapTourId) {
        throw new Error("Choisir un tableau et un tour");
      }
      const result = await callSpiddDraftBatchRemap({
        tournoiId,
        draftIds: selectedRemappableIds,
        firestoreTableauId: remapTableauId,
        firestoreTourId: remapTourId,
      });
      setRemapOpen(false);
      if (result.errors.length) {
        setError(
          `${result.updated.length} mises à jour, ${result.errors.length} erreurs`
        );
      }
    });

  if (!tournoiId) {
    return <Alert severity="warning">Tournoi courant introuvable.</Alert>;
  }

  const readyCount = filtered.filter(isBatchCreatable).length;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip label={`Total ${summary.total}`} />
        <Chip color="primary" label={`Nouvelles ${summary.proposed}`} />
        <Chip color="warning" label={`Anomalies ${summary.needsReview}`} />
        <Chip color="error" label={`Conflits ${summary.conflicts}`} />
        <Chip
          color="success"
          label={`Associées ${summary.linked + summary.approved}`}
        />
        <Chip label={`Ignorées ${summary.ignored}`} />
        <Chip label={`Obsolètes ${summary.stale}`} />
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Tableau</InputLabel>
          <Select
            label="Tableau"
            value={filters.tableauId}
            onChange={(e) =>
              setFilters((f) => ({ ...f, tableauId: e.target.value }))
            }
          >
            <MenuItem value="">Tous</MenuItem>
            {tableauOptions.map(([id, label]) => (
              <MenuItem key={id} value={id}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Phase</InputLabel>
          <Select
            label="Phase"
            value={filters.phase}
            onChange={(e) =>
              setFilters((f) => ({ ...f, phase: e.target.value }))
            }
          >
            <MenuItem value="">Toutes</MenuItem>
            {phaseOptions.map((id) => (
              <MenuItem key={id} value={id}>
                {id === "POULE" ? "Poules" : id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Poule</InputLabel>
          <Select
            label="Poule"
            value={filters.poolId}
            onChange={(e) =>
              setFilters((f) => ({ ...f, poolId: e.target.value }))
            }
          >
            <MenuItem value="">Toutes</MenuItem>
            {poolOptions.map(([id, label]) => (
              <MenuItem key={id} value={id}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Statut</InputLabel>
          <Select
            label="Statut"
            value={filters.status}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                status: e.target.value as SpiddDraftFilters["status"],
              }))
            }
          >
            <MenuItem value="ACTIVE">À traiter</MenuItem>
            <MenuItem value="PROPOSED">Proposées</MenuItem>
            <MenuItem value="NEEDS_REVIEW">À revoir</MenuItem>
            <MenuItem value="CONFLICT">Conflits</MenuItem>
            <MenuItem value="APPROVED">Approuvées</MenuItem>
            <MenuItem value="LINKED">Associées</MenuItem>
            <MenuItem value="IGNORED">Ignorées</MenuItem>
            <MenuItem value="STALE">Obsolètes</MenuItem>
            <MenuItem value="ALL">Toutes</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Joueur ou licence"
          value={filters.query}
          onChange={(e) =>
            setFilters((f) => ({ ...f, query: e.target.value }))
          }
        />
        <Button
          variant="contained"
          color="primary"
          disabled={busy}
          onClick={handleSync}
          sx={{
            fontWeight: 700,
            minHeight: 40,
            px: 2,
          }}
        >
          {busy ? "Synchronisation…" : "Resynchroniser"}
        </Button>
        <Tooltip title="Coche toutes les propositions visibles qui peuvent être créées en lot (sans anomalie bloquante).">
          <span>
            <Button
              variant="outlined"
              color="inherit"
              disabled={readyCount === 0}
              onClick={selectReadyVisible}
              sx={{
                fontWeight: 600,
                minHeight: 40,
                borderWidth: 1.5,
                color: "text.primary",
                borderColor: "text.secondary",
                "&:hover": {
                  borderWidth: 1.5,
                  borderColor: "text.primary",
                  bgcolor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "action.hover",
                },
                "&.Mui-disabled": {
                  color: "text.disabled",
                  borderColor: "divider",
                },
              }}
            >
              Sélectionner les prêtes ({readyCount})
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          color="inherit"
          disabled={busy || selectedRemappableIds.length === 0}
          onClick={openRemapDialog}
          sx={{
            fontWeight: 600,
            minHeight: 40,
            borderWidth: 1.5,
            color: "text.primary",
            borderColor: "text.secondary",
          }}
        >
          Corriger tableau / tour ({selectedRemappableIds.length})
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={busy || selected.size === 0}
          onClick={() => setBatchConfirmOpen(true)}
          sx={{ fontWeight: 700, minHeight: 40 }}
        >
          Créer les matchs sélectionnés ({selected.size})
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading && <Typography>Chargement des propositions…</Typography>}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          overflowX: "auto",
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{ bgcolor: "background.paper", borderBottomColor: "divider" }}
              />
              {SORTABLE_COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  sortDirection={sortKey === column.id ? sortDir : false}
                  sx={{
                    bgcolor: "background.paper",
                    color: "text.primary",
                    borderBottomColor: "divider",
                    fontWeight: 600,
                  }}
                >
                  <TableSortLabel
                    active={sortKey === column.id}
                    direction={sortKey === column.id ? sortDir : "asc"}
                    onClick={() => handleSort(column.id)}
                    sx={{ color: "inherit" }}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell
                align="right"
                sx={{
                  bgcolor: "background.paper",
                  color: "text.primary",
                  borderBottomColor: "divider",
                  fontWeight: 600,
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((draft) => {
              const creatable = isBatchCreatable(draft);
              const tableauLabel = getTableauLabel(draft);
              const playersLabel = getPlayersLabel(draft);
              return (
                <TableRow
                  key={draft.id}
                  hover
                  selected={selected.has(draft.id)}
                  onClick={() => setDetail(draft)}
                  sx={{
                    cursor: "pointer",
                    color: "text.primary",
                    borderBottomColor: "divider",
                    "&.Mui-selected": {
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "action.selected",
                    },
                    "&.Mui-selected:hover": {
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.12)"
                          : "action.hover",
                    },
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selected.has(draft.id)}
                      disabled={!creatable && !isRemappable(draft)}
                      onChange={() => toggleSelect(draft.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{tableauLabel}</Typography>
                  </TableCell>
                  <TableCell>{draftTypeLabel(draft.type)}</TableCell>
                  <TableCell>{draftPhaseLabel(draft)}</TableCell>
                  <TableCell>{playersLabel}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_LABELS[draft.status] || draft.status}
                      color={
                        draft.status === "NEEDS_REVIEW"
                          ? "warning"
                          : draft.status === "PROPOSED"
                            ? "primary"
                            : draft.status === "STALE"
                              ? "default"
                              : "success"
                      }
                      sx={{
                        fontWeight: 600,
                        ...(draft.status === "STALE" ||
                        draft.status === "IGNORED"
                          ? {
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.12)"
                                  : "action.selected",
                              color: "text.primary",
                            }
                          : {}),
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {draftSuggestionLabel(draft.matchSuggestion)}
                  </TableCell>
                  <TableCell>
                    {(draft.anomalies || []).length
                      ? draft.anomalies.map((a) => a.code).join(", ")
                      : "—"}
                  </TableCell>
                  <TableCell
                    align="right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                    >
                      {draft.matchSuggestion === "LINK" &&
                        draft.suggestedOperationalMatchId &&
                        !draft.operationalMatchId && (
                          <Button
                            size="small"
                            disabled={busy}
                            onClick={() => void handleLink(draft)}
                          >
                            Associer
                          </Button>
                        )}
                      {creatable && (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={busy}
                          onClick={() => void handleCreate(draft)}
                        >
                          Créer
                        </Button>
                      )}
                      {!draft.operationalMatchId &&
                        draft.status !== "IGNORED" && (
                          <Button
                            size="small"
                            disabled={busy}
                            onClick={() => void handleIgnore(draft)}
                          >
                            Ignorer
                          </Button>
                        )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography color="text.secondary">
                    Aucune proposition pour ces filtres. Vérifiez la
                    configuration SPIDD puis resynchronisez.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={remapOpen}
        onClose={() => setRemapOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Corriger tableau / tour</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography>
              Appliquer le même tableau et le même tour Firestore à{" "}
              {selectedRemappableIds.length} proposition(s) sélectionnée(s),
              avant de créer les matchs. La source SPIDD n’est pas modifiée.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Tableau Firestore</InputLabel>
              <Select
                label="Tableau Firestore"
                value={remapTableauId}
                onChange={(e) => setRemapTableauId(String(e.target.value))}
              >
                {sortedBoards.map((board) => (
                  <MenuItem key={String(board.id)} value={String(board.id)}>
                    {boardDisplayName(board)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Tour Firestore</InputLabel>
              <Select
                label="Tour Firestore"
                value={remapTourId}
                onChange={(e) => setRemapTourId(String(e.target.value))}
              >
                {sortedRounds.map((tour) => (
                  <MenuItem key={String(tour.id)} value={String(tour.id)}>
                    {tour.label || tour.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemapOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            disabled={busy || !remapTableauId || !remapTourId}
            onClick={() => void handleBatchRemap()}
          >
            Appliquer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={batchConfirmOpen}
        onClose={() => setBatchConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Création en lot</DialogTitle>
        <DialogContent>
          <Typography>
            {
              Array.from(selected).filter((id) => {
                const d = drafts.find((x) => x.id === id);
                return d && isBatchCreatable(d);
              }).length
            }{" "}
            match(s) opérationnel(s) seront créés au statut CREE. Les
            propositions avec anomalies sont exclues.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchConfirmOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => void handleBatchCreate()}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Détail de la proposition</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Tableau</strong> {getTableauLabel(detail)}
              </Typography>
              <Typography variant="body2">
                <strong>Type</strong> {draftTypeLabel(detail.type)}
              </Typography>
              <Typography variant="body2">
                <strong>Poule / tour</strong> {draftPhaseLabel(detail)}
              </Typography>
              <Typography variant="body2">
                <strong>Joueurs</strong> {getPlayersLabel(detail)}
              </Typography>
              <Typography variant="body2">
                <strong>Licences</strong> {detail.playerLicences.join(", ")}
              </Typography>
              <Typography variant="body2">
                <strong>Parties SPIDD</strong>{" "}
                {(detail.sourcePartieIds || []).join(", ") || "—"}
              </Typography>
              <Typography variant="body2">
                <strong>Suggestion</strong>{" "}
                {draftSuggestionLabel(detail.matchSuggestion)}
                {detail.suggestedOperationalMatchId
                  ? ` (${detail.suggestedOperationalMatchId})`
                  : ""}
              </Typography>
              {(detail.anomalies || []).length > 0 && (
                <Alert severity="warning">
                  {(detail.anomalies || [])
                    .map((a) => `${a.code}${a.detail ? `: ${a.detail}` : ""}`)
                    .join(" · ")}
                </Alert>
              )}
              {detail.meta?.groupingAmbiguous && (
                <Alert severity="info">
                  Regroupement d&apos;occupation suggéré (modifiable). Ce
                  n&apos;est pas une contrainte du tirage SPIDD.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SpiddDraftsPanel;
